<template>
    <main v-if="data && data.page">
        <div class="splashZone">
            <div class="infoBar">
                <WebsiteBreadcrumbs>
                    <a>About</a>
                </WebsiteBreadcrumbs>
            </div>
            <h1 class="helvetica title1">{{ data?.page?.title }}</h1>

            <div class="titleHolder">
                <div class="titleCont">
                    <div class="title helvetica">{{ data?.page?.summary }}</div>
                </div>
            </div>

            <div class="splashImage" v-for="image in data?.page.splash_images">
                <div class="overlay"></div>
                <CopyrightedImage :image="image.directus_files_id" :width="1200" :mobileHeight="1200" :cover="true" />
            </div>
        </div>

        <section class="introduction" v-if="data && data.page">
            <div class="body" v-if="data.page.introduction" v-html="data.page.introduction"></div>
            <div class="downloadsCont">
                <BlockDownloads :downloads="data?.page.downloads.map((d: any) => d.directus_files_id)" />
            </div>
        </section>

        <section class="message" v-for="m in data?.page.messages">
            <div class="intro">
                <h2 class="baron headerText">{{ m.title }}</h2>
            </div>
            <div class="bigitalic" v-html="m.message"></div>
            <ItemPerson :person="m" />
        </section>

        <section v-if="data && data.page">
            <div class="intro">
                <h2 class="baron headerText">Board</h2>
                <div class="body" v-html="data.page.board_introduction"></div>
            </div>
            <div class="members">
                <ItemMemberBoard v-for="member in data.page.board_members.filter((m: any) => m.row != 'all')" :member="member" />
            </div>
            <div class="members">
                <ItemMemberBoard v-for="member in data.page.board_members.filter((m: any) => m.row == 'all')" :member="member" />
            </div>
        </section>
        <section v-if="data && data.page && data.federations">
            <div class="intro">
                <h2 class="baron headerText">Member Federations</h2>
                <div class="body" v-html="data.page.member_federations_introduction"></div>
            </div>
            <div class="federations">
                <ItemMemberCountry v-for="member in data.federations.sort((a: any, b: any) => a.country.localeCompare(b.country))" :member="member" />
            </div>
        </section>
        <section v-if="data && data.page && data.federations">
            <div class="intro">
                <h2 class="baron headerText">Corporate Members</h2>
                <div class="body" v-html="data.page.corporate_members_introduction"></div>
            </div>
            <div class="federations">
                <ItemMemberCorporation v-for="member in data.page.corporate_members" :member="member" />
            </div>
        </section>
        <section v-if="data && data.page">
            <div class="intro">
                <h2 class="baron headerText">Secretariat</h2>
            </div>
            <div class="members">
                <ItemMemberBoard v-for="member in data.page.secretariat_members" :member="member" />
            </div>
        </section>
        <section v-if="data && data.page">
            <div class="intro">
                <h2 class="baron headerText">Associated company members</h2>
                <div class="body" v-html="data.page.associated_companies_introduction"></div>
            </div>
            <div class="associatedmembers">
                <div class="company" v-for="member in data.page.associated_company_members">
                    <img :src="getDirectusThumbnail(member.logo.id)" :alt="member.name" />
                    <a :href="member.link" class="overlink" target="_blank" rel="noreferer noopener"></a>
                </div>
            </div>
        </section>
        <WebsiteFooter />
    </main>
</template>

<script lang="ts" setup>
const { getWhoWeArePage } = usePages();
const { getDirectusThumbnail } = useImages();
const directusUrl = useDirectusUrl()
const { data } = useAsyncData('about-who-we-are', () => getWhoWeArePage());

useSeoMeta({
    title: () => `${data.value?.page?.title} | About | EIC Federation`,
    ogTitle: () => `${data.value?.page?.title} | About | EIC Federation`,
    twitterTitle: () => `${data.value?.page?.title} | About | EIC Federation`,

    description: () => data.value?.page?.summary,
    ogDescription: () => data.value?.page?.summary,
    twitterDescription: () => data.value?.page?.summary,

    ogImage: () => data.value?.page?.cover?.id ? directusUrl + '/assets/' + data.value?.page.cover.id : undefined,
})

</script>

<style lang="scss" scoped>
main {

    display: flex;
    flex-direction: column;
    gap: var(--size-gutter-sections);

    .splashZone {
        position: relative;
        padding-top: var(--size-padding-header);

        .infoBar {
            padding: 0 var(--size-margin-page);
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: var(--size-margin-page);

            margin-bottom: var(--size-gutter-default);
            color: var(--color-primary);
        }

        h1 {
            margin-bottom: var(--size-gutter-double);
            padding: 0 var(--size-margin-page);
        }

        .splashImage {
            position: sticky;
            top: 0;
            width: 100%;
            height: 100vh;

            .overlay {
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                z-index: 1;
                background-color: rgba(0, 0, 0, 0.15);
                mix-blend-mode: multiply;
            }
        }

        .titleHolder {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            height: 100%;
            z-index: 2;

            .titleCont {
                position: sticky;
                top: 0;
                width: 100%;
                height: 100vh;
                box-sizing: border-box;
                display: flex;
                align-items: flex-end;
                padding: var(--size-margin-page);
                padding-right: var(--size-gutter-default);
                overflow: hidden;

                color: white;

                .title {
                    text-wrap: balance;
                    font-size: clamp(24px, 5vw, 60px);
                    line-height: clamp(24px, 5vw, 60px);
                }
            }
        }
    }

    .introduction {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: var(--size-gutter-double) var(--size-margin-page);

        .body,
        .downloadsCont {
            grid-column: 2;
        }
    }

    section {
        padding: 0 var(--size-margin-page);

        display: flex;
        flex-direction: column;
        gap: var(--size-gutter-large);

        .intro {
            display: flex;
            flex-direction: column;
            gap: var(--size-gutter-double);

            h2 {
                border-top: var(--size-thickness) solid var(--color-primary);
                font-weight: normal;
                color: var(--color-primary);
                padding-top: var(--size-margin-page);
            }
        }

        .members {
            display: grid;
            grid-template-columns: repeat(6, minmax(0, 1fr));
            gap: var(--size-margin-page);
        }

        .federations {
            display: grid;
            grid-template-columns: repeat(5, minmax(0, 1fr));
            gap: var(--size-gutter-large) var(--size-margin-page);
        }

        .associatedmembers {
            display: grid;
            grid-template-columns: repeat(6, minmax(0, 1fr));
            gap: var(--size-gutter-large) var(--size-margin-page);


            .company {
                position: relative;
                border-left: var(--size-thickness) solid var(--color-primary);
                padding-left: var(--size-gutter-default);

                img {
                    height: 100px;
                    width: 100px;
                    object-fit: contain;
                    object-position: center;
                }
            }
        }

        &.message {
            gap: var(--size-gutter-double);
        }
    }
}


@media screen and (max-width: 1024px) {

    main section {


        .members {
            display: grid;
            grid-template-columns: repeat(6, minmax(0, 1fr));
            gap: var(--size-margin-page);

            >* {
                grid-column: span 2;
            }
        }

        .associatedmembers {
            display: grid;
            grid-template-columns: repeat(3, minmax(0, 1fr));
            gap: var(--size-margin-page);
        }


    }

}



@media screen and (max-width: 600px) {

    main section {
        gap: 0;

        .members {
            padding-top: var(--size-gutter-double);
            display: block !important;
        }

        .federations {
            display: flex;
            flex-direction: column;
            padding-top: var(--size-gutter-double);
            gap: 0;
        }

        .associatedmembers {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: var(--size-margin-page);
        }


    }

    main .introduction {
        display: grid;
        grid-template-columns: repeat(1, minmax(0, 1fr));
        gap: var(--size-gutter-double) var(--size-margin-page);

        .body,
        .downloadsCont {
            grid-column: 1 / span 2;
        }
    }

    // .title {
    //     text-wrap: balance;
    //     font-size: var(--text-size-title1);
    //     line-height: var(--text-line-title1);
    // }

}
</style>